import { LinkButton, Pagination } from '@pmi/dsm-react';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { CommentItem } from '../../../../shared/models/CommentItem';
import commentStyle from './agreementComments.module.scss';
import timeAgo from '../../../../shared/utils/timeAgo';
import {
  useCommentReadMutation,
  useGetCommentsByDealIdQuery,
} from './apiSlice';
import { AgreementApiModel } from '../../../../shared/models/DealModel';
import useUser from '../../../../shared/hooks/useUser';
import usePermissions from '../../../../shared/hooks/usePermissions';
import PERMISSIONS from '../../../../shared/permissions';

const SYSTEM_AUTHOR = 'System';
const ACCOUNT_INACTIVE_DESCRIPTION = 'Account is inactive';
const ACCOUNT_DOWNGRADED_SUBSCRIPTION =
  'Account downgraded to a restricted tier';

const DATE_TIME_FORMAT: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: 'long',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  timeZoneName: 'short',
};

const getCommentDescription = (item: CommentItem) => {
  if (item.personFullName === SYSTEM_AUTHOR) {
    const localDateTime = new Date(item.createdDate).toLocaleString(
      undefined,
      DATE_TIME_FORMAT,
    );
    if (item.description === ACCOUNT_INACTIVE_DESCRIPTION) {
      return `Account has been downgraded to a free subscription on ${localDateTime}`;
    }
    if (item.description === ACCOUNT_DOWNGRADED_SUBSCRIPTION) {
      return `Agreement auto-disabled due to ATP downgrading to a restricted tier as of ${localDateTime}`;
    }
  }
  return item.description;
};

export interface CommentsListProps {
  deal: AgreementApiModel;
  accountId: string;
  setEditComment: Dispatch<SetStateAction<CommentItem | null>>;
  setDeleteComment: Dispatch<SetStateAction<CommentItem | null>>;
}

export default function CommentsList({
  deal,
  accountId,
  setEditComment,
  setDeleteComment,
}: CommentsListProps) {
  const { hasAccess } = usePermissions();
  const isInternalAdmin = hasAccess({
    permissions: [PERMISSIONS.ManageElevatedAdmins],
  });
  const [commentRead, commentReaded] = useCommentReadMutation();
  const [commentList, setCommentList] = useState<CommentItem[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [hasReaded, setHasReaded] = useState<boolean>(false);
  const [currentPageSize, setCurrentPageSize] = useState<number>(
    Number(localStorage.getItem('pageSize') ?? 10),
  );
  const user = useUser();
  const currentPersonId = user?.person_id?.toString();
  const isOwnComment = (item: CommentItem) =>
    Boolean(currentPersonId) && item.personId?.toString() === currentPersonId;
  const commentRowContents = (item: CommentItem) => (
    <>
      <div className={`small ${commentStyle.commentHeader}`}>
        <div className={commentStyle.commentAuthor}>
          <span className="fw-semibold">{item.personFullName}</span>
        </div>
        <div className={commentStyle.commentAt}>
          {timeAgo(new Date(item.createdDate))}
        </div>
      </div>
      <div className={commentStyle.commentContents}>
        {getCommentDescription(item)}
      </div>
      {isOwnComment(item) && (
        <div className={commentStyle.commentControls}>
          <LinkButton
            titleText="Edit"
            size="sm"
            className={`fw-normal px-0 text-decoration-underline ${commentStyle.text600}`}
            onClick={() => setEditComment(item)}
          />
          <span className={commentStyle.text300}>|</span>
          <LinkButton
            titleText="Delete"
            size="sm"
            className={`fw-normal px-0 text-decoration-underline ${commentStyle.text600}`}
            onClick={() => setDeleteComment(item)}
          />
        </div>
      )}
    </>
  );
  const {
    data,
    isLoading: isCommentsLoading,
    isFetching: isCommentsFetching,
  } = useGetCommentsByDealIdQuery({
    dealId: deal.id,
    accountId,
    page: currentPage,
    pageSize: currentPageSize,
  });

  useEffect(() => {
    if (data && data.items && Array.isArray(data.items)) {
      const temp = [...data.items];
      if (isInternalAdmin && currentPersonId) {
        const hasNewMessages = temp.find(
          (t) => !t.isRead && t.personId?.toString() !== currentPersonId,
        );
        if (hasNewMessages && !hasReaded) {
          commentRead({ accountId, dealId: hasNewMessages.dealId })
            .unwrap()
            .then(() => {
              commentReaded.reset();
            });
        }
        setHasReaded(true);
      }
      setCommentList(temp);
    }
  }, [isCommentsLoading, isCommentsFetching, user]);

  useEffect(() => {
    if (data) {
      setTotalPages(Math.ceil(data.totalItemCount / currentPageSize));
    }
  }, [data, currentPageSize]);

  if (isCommentsLoading || isCommentsFetching) {
    return <p>Loading comments...</p>;
  }

  return (
    <div className={commentStyle.commentRows}>
      {commentList.length === 0 && (
        <p className={commentStyle.text600}>
          No comments have been added to this agreement
        </p>
      )}
      {commentList.length > 0 &&
        commentList.map((item) => (
          <div
            className={commentStyle.commentRow}
            key={`comment-${item.id.toString()}`}
          >
            {commentRowContents(item)}
          </div>
        ))}
      {commentList.length > 0 && totalPages > 1 && (
        <div className="d-flex justify-content-center">
          <Pagination
            currentPage={data?.currentPage}
            disabled={false}
            onChange={(e: unknown, pageNumber: number) => {
              setCurrentPage(pageNumber);
            }}
            onPageSizeChange={(pageSize: number) => {
              setCurrentPageSize(pageSize);
              localStorage.setItem('pageSize', pageSize.toString());
            }}
            pageSize={currentPageSize}
            pageSizes={[
              {
                label: '10 per page',
                value: '10',
              },
              {
                label: '25 per page',
                value: '25',
              },
              {
                label: '50 per page',
                value: '50',
              },
              {
                label: '100 per page',
                value: '100',
              },
            ]}
            totalPages={totalPages}
          />
        </div>
      )}
    </div>
  );
}
